/**
 * Valid statuses:
 *
 * `cancelled`: This status is self-explanatory, indicating that the loop has been canceled.
 * `completed-with-errors`: If a fatal error occurs during the loop execution.
 * `completed`: Indicates that the loop has finished its execution successfully.
 * `in-progress`: Indicates that the loop is currently being executed.
 * `not-completed`: If the recipe hasn't been fully triggered yet. E.g. two triggers but only one has been fired.
 * `paused`: Indicates that the loop has been temporarily paused.
 * `queued`: Indicates that the current loop is scheduled to start at a later time.
 * `skipped`: This status is assigned when the loop has no users/posts to iterate.
 */

import './in-progress';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
const { isEmpty, sprintf } = _uo.utility;

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logLoop.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-status' )
export class AutomatorLogItemLoopStatus extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The post ID of the loop
	@property( { type: Number, attribute: 'loop-id' } ) loopID;

	// A unique code created to detect this specific loop run in this specific recipe run
	// The backend will use this to cancel/pause/resume the loop
	@property( { type: String, attribute: 'loop-process-code' } ) processCode;

	@property( { type: String, attribute: 'status-id' } ) statusID;

	@property( { type: Number, attribute: 'elements-completed' } ) elementsCompleted = 0;

	@property( { type: Number, attribute: 'elements-total' } ) elementsTotal;

	// Any message that the backend could have returned about this loop
	// This will be used to show errors too
	@property( { type: String, attribute: false } ) message = '';

	// The date the loop started running
	@property( { type: String, attribute: 'start-date' } ) startDate;

	// The date the loop finished running
	@property( { type: String, attribute: 'end-date' } ) endDate;

	// The difference between both start and end dates
	@property( { type: Object, attribute: false } ) dateElapsed;

	// Information about when the next batch of this loop will run
	@property( { type: Object, attribute: false } ) dateNextProcess;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		if ( this.statusID === 'in-progress' ) {
			return html`
				<uap-log-loop-status-in-progress
					loop-id=${ this.loopID }
					loop-process-code=${ this.processCode }

					elements-completed=${ this.elementsCompleted }
					elements-total=${ this.elementsTotal }
					start-date=${ this.startDate }

					.dateElapsed=${ this.dateElapsed }
					.dateNextProcess=${ this.dateNextProcess }

					?import-mode=${ this.isImportMode }
				></uap-log-loop-status-in-progress>
			`;
		}

		return html`
			<div class="progress progress--${ this.statusID }">
				<div class="progress-info">
					${ this._templateStatus }
					${ this._templateNumberProcessed }
				</div>

				<div class="progress-details">
					${ this._templateDate }
					${ this._templateMessage }
				</div>
			</div>
		`;
	}

	/**
	 * Creates the status icon and text
	 *
	 * @return {TemplateResult} The status
	 */
	get _templateStatus() {
		if ( this.statusID === 'completed' ) {
			return html`
				<span class="progress-info-status">
					<uap-log-status status-id=${ this.statusID } hide-label></uap-log-status>
				</span>
			`;
		}

		return html`
			<span class="progress-info-status">
				<uap-log-status status-id=${ this.statusID }></uap-log-status>
			</span>
		`;
	}

	/**
	 * Render the number of elements processed
	 *
	 * @return {TemplateResult} The number of elements processed
	 */
	get _templateNumberProcessed() {
		// Check if there is data available
		if ( isEmpty( this.elementsTotal ) ) {
			return;
		}

		// Check if this was partially completed
		if ( [ 'cancelled', 'paused' ].includes( this.statusID ) ) {
			// Show "{x} processed". x = total items
			return html`
				<div class="progress-info-text">
					<span class="progress-info-completed">
						${ sprintf(
							strings.progressStatus,
							this.elementsCompleted.toLocaleString(),
							this.elementsTotal.toLocaleString()
						) }
					</span>
					<span class="progress-info-percentage">
						(${ this.percentage }%)
					</span>
				</div>
			`;
		}

		// Show "{x} processed". x = total items
		return html`
			<div class="progress-info-text">
				<span class="progress-info-completed">
					${ sprintf(
						strings.progressStatusCompleted,
						this.elementsTotal.toLocaleString()
					) }
				</span>
			</div>
		`;
	}

	/**
	 * Returns the HTML of the section that shows the start and end date
	 *
	 * @return {TemplateResult|undefined} The date
	 */
	get _templateDate() {
		// Check if the item hasn't run yet
		if ( isEmpty( this.startDate ) ) {
			return;
		}

		// If completed, show the relative date and hide the start and end date in a tooltip
		if (
			this.statusID === 'completed' &&
			! isEmpty( this.dateElapsed ) &&
			! isEmpty( this.dateElapsed.human_time_diff )
		) {
			return html`
				<uo-tooltip>
					${ sprintf(
						strings.dateRange,
						this.startDate,
						this.endDate
					) }

					<div slot="target">
						<uo-icon id="clock"></uo-icon>
						${ strings.timeElapsed } ${ this.dateElapsed.human_time_diff }
					</div>
				</uo-tooltip>
			`;
		}

		// Otherwise, just show the start date
		return html`
			<div>
				<uo-icon id="clock"></uo-icon>
				${ this.startDate }
			</div>
		`;
	}

	/**
	 * Returns the template that shows the status message, if available.
	 *
	 * @return {TemplateResult} The date
	 */
	get _templateMessage() {
		// Check if the item hasn't run yet
		if ( isEmpty( this.message ) ) {
			return;
		}

		return html`
			<div>
				<uo-icon id="note"></uo-icon>
				${ this.message }
			</div>
		`;
	}

	/**
	 * Calculates the percentage using the completed and total elements
	 *
	 * @return {Number} The percentage
	 */
	get percentage() {
		return Math.round( 100 * this.elementsCompleted / this.elementsTotal );
	}
}